import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from '../../../../utils/cn';
import { isEmpty, noop } from 'lodash';
import Icon from '../../Icon/Icon';
import Addresses from '../../../utils/Addresses';
import ContactInformation from '../../Contact/ContactInformation/ContactInformation';
import HoursOfOperation from '../../HoursOfOperation/HoursOfOperation';
import Arrays from '../../../utils/Arrays';
import ServiceTypes from '../../../utils/ServiceTypes';
import ProviderCardAddRemoveButton from '../components/ProviderCardAddRemoveButton/ProviderCardAddRemoveButton';
import Popover from '../../Popover/Popover';
import InfoPanel from '../../InfoPanel/InfoPanel';

const ProviderCard = (props) => {
  const {
    id,
    className = '',
    disableSelection = false,
    isSelected = false,
    provider,
    selectedServiceType,
    unselectable = false,
    serve = false,
    tooltipText = undefined,
  } = props;

  const calculatedDistances = provider.addresses.map(address => Addresses.calculateDistance(address, props.originLatLng));
  const [closestDistanceAddress, setClosestDistanceAddress] = useState(Addresses.findClosestDistanceAddress(calculatedDistances));
  const [originLatLng, setOriginLatLng] = useState(props.originLatLng);

  useEffect(() => {
    if (props.originLatLng[0] !== originLatLng[0] || props.originLatLng[1] !== originLatLng[1]) {
      const updateDistances = props.provider.addresses.map(address => addresses.calculateDistance(address, nextProps.originLatLng));

      const updatedClosestDistanceAddress = Addresses.findClosestDistanceAddress(updateDistances);
      setClosestDistanceAddress({ updatedClosestDistanceAddress });
      setOriginLatLng(props.originLatLng);
    };
  }, [props.originLatLng, props.provider]);

  const onAddProvider = (destinationLatLng) => {
    const distance = Addresses.getDistanceInMiles(originLatLng, destinationLatLng, {
      format: 'float',
    });

    const providerWithDistance = { ...provider, distance };

    props.onAddProvider(providerWithDistance);
  }

  const onRemoveProvider = () => {
    props.onRemoveProvider(provider);
  }

  const onDetailClick = () => {
    props.onDetailClick(provider);
  }

  const generateMoreAddressCountText = () => {
    let text;

    const { addressLength } = provider.addresses.length;
    if (addressLength > 1) {
      text = `${addressLength - 1} more`;
    }
    return text;
  }

  const detailsProps = !props.onDetailClick ? {} : {
    onClick: onDetailClick,
    onKeyPress: onDetailClick,
    role: 'button',
    tabIndex: 0,
  };

  const isInNetworkGroup = provider.licensed;
  const providerServiceTypes = provider.service_types;

  const serviceTypes = ServiceTypes.matchingServiceTypes(selectedServiceType, providerServiceTypes);
  const svcTypeBanner = ServiceTypes.serviceTypeBanner(serviceTypes);

  const {
    email_addresses,
    hours,
    phone_numbers,
    website_url,
  } = provider;

  const showContactInfo =
    !isEmpty(email_addresses) ||
    !isEmpty(phone_numbers) ||
    !isEmpty(website_url);

  const addressCount = generateMoreAddressCountText();
  const location = {
    hours,
    ...closestDistanceAddress,
  };

  const cardClass = () => cn('flex flex-row flex-no-wrap w-full', className);

  return (
    <div className="border border-solid border-table-border-color">
      <Popover
        tooltipText={tooltipText}
        id={id}
        hiddenTooltip={!serve || !tooltipText}
        childrenClassName="w-full flex-4"
      >
        <div
          className={cardClass()}
          id={`anchor-id-${id}`}
        >
          <div className={
            cn('w-full text-text-blue', {
              'opacity-50': disableSelection,
            })
          }
          >
            <div className={cn("p-4", { 'cursor-default': disableSelection })} {...detailsProps}>
              {
                serve && (
                  <div className="">
                    <Icon
                      className="float-right"
                      color="dark-border-grey"
                      icon="Radar"
                      size={82}
                    />
                    <div className="mb-4 flex">
                      <Icon
                        className="my-auto"
                        color={`${isInNetworkGroup ? 'united-blue' : 'brand-blue'}`}
                        icon="Radar"
                        size={16}
                      />
                      <InfoPanel message="Serves this area" showIcon={false} />
                    </div>
                  </div>
                )
              }
              {
                (!isInNetworkGroup) && (
                  <div className="mb-1 text-sm flex">
                    <Icon
                      className="mr-2 my-auto"
                      color="brand-blue"
                      icon="OutOfNetwork"
                    />
                    <p>
                      Out of Network Organization
                    </p>
                  </div>
              )}
              <h4 className="text-xl mb-2">{provider.name}</h4>
              {
                !isEmpty(svcTypeBanner) && (
                  <p className="text-sm text-purple mb-3">
                    {svcTypeBanner}
                  </p>
              )}
              {
                showContactInfo && (
                  <ContactInformation
                    addressCount={addressCount}
                    addresses={[location]}
                    email_addresses={email_addresses}
                    hideLabels
                    hours={hours}
                    isInNetworkGroup={isInNetworkGroup}
                    originLatLng={originLatLng}
                    phone_numbers={[Arrays.findPrimaryOrFirst(phone_numbers)]}
                    showContactInfoHeader={false}
                    showLocationHours={false}
                    website_url={website_url}
                    serve={serve}
                  />
              )}
              <HoursOfOperation
                hours={location.hours || location.hours_of_operation}
                isInNetworkGroup={isInNetworkGroup}
                showAll={false}
                showHoursIcon
              />
            </div>
          </div>
          {
            !disableSelection && !unselectable && (
              <ProviderCardAddRemoveButton
                destinationLatLng={[closestDistanceAddress.latitude, closestDistanceAddress.longitude]}
                distance={closestDistanceAddress.distance}
                onAddClick={onAddProvider}
                onRemoveClick={onRemoveProvider}
                selected={isSelected}
              />
          )}
        </div>
      </Popover>
    </div>
  );
}

ProviderCard.propTypes = {
  /** identifier for each card */
  id: PropTypes.string.isRequired,
  /** className */
  className: PropTypes.string,
  /** true for disabled card state, hidden add/remove button */
  disableSelection: PropTypes.bool,
  /** true indicates this provider is selected */
  isSelected: PropTypes.bool,
  /** called on add button click */
  onAddProvider: PropTypes.func,
  /** called on provider card click */
  onDetailClick: PropTypes.func,
  /** called on remove button click */
  onRemoveProvider: PropTypes.func,
  /** center of map to calculate distance */
  originLatLng: PropTypes.array,
  /** object containing provider data */
  provider: PropTypes.shape({
    addresses: PropTypes.array,
    email_addresses: PropTypes.array,
    group_type: PropTypes.string,
    hours: PropTypes.object,
    licensed: PropTypes.bool,
    name: PropTypes.string,
    phone_numbers: PropTypes.array,
    service_types: PropTypes.array,
    website_url: PropTypes.string,
  }).isRequired,
  /** service type object or array of service type objects */
  selectedServiceType: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    ),
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  ]).isRequired,
  /** true to hide add/remove buttons */
  unselectable: PropTypes.bool,
  /** true to hide location field and show map icon with serve this area label */
  serve: PropTypes.bool,
  /** text to show when using the popover as a tooltip */
  tooltipText: PropTypes.string,
};

ProviderCard.defaultProps = {
  className: '',
  disableSelection: false,
  isSelected: false,
  onAddProvider: noop,
  onDetailClick: noop,
  onRemoveProvider: noop,
  originLatLng: [],
  unselectable: false,
  serve: false,
  tooltipText: undefined,
};

export default ProviderCard;
